/**
 * Premium (Classic 50/50) style-specific section renderers.
 * Extracted from Classic5050StylePremium.jsx for use in GridEngine.
 */
import React from 'react'
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import ResponsiveImage from '../../components/ResponsiveImage'
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Star from 'lucide-react/dist/esm/icons/star.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import InlineTranslationEdit from '../../components/InlineTranslationEdit'
import HeroSiteLogo from '../../components/HeroSiteLogo'
import CountdownTimerStyles from '../../components/CountdownTimerStyles'
import DrawCountdownHeader from '../../components/DrawCountdownHeader'
import useHeroContent from '../../hooks/useHeroContent'
import RecentWinners from '../../components/RecentWinners'
import { SplitPotCharity } from '../../components/charity'
import { useCharityTheme } from '../../contexts/CharityThemeContext'
import { useSiteConfig } from '../../contexts/SiteConfigContext'
import { charityConfig } from '../../config/charity'
import HeroPartnerGlassSelect from '../../components/HeroPartnerGlassSelect'
import DrawLifecycleMessage from '../../components/DrawLifecycleMessage'
import { formatCurrencyValue } from '../../utils/formatCurrency'

// Premium Hero: Gradient background, charity logo, animated orbs, split pot card
export function PremiumHeroSection({
  mainDraw, partner, t, editMode, handleTranslationUpdate, gameType = 'classic5050', sections,
  purchaseNavigate, timeLeft, shouldAnimate, countdownStyle, showPartnerSelector = true,
  lifecycleState = null, purchasesBlocked = false, language,
}) {
  const { branding } = useCharityTheme()
  const { siteDetails } = useSiteConfig() || {}
  const showPartner = showPartnerSelector
  const c = t
  const heroContent = useHeroContent({
    mainDraw,
    partner,
    gameType,
    sections,
    fallbackTitle: gameType === 'chaseTheAce' ? 'Chase the Ace' : (gameType === 'modified5050' ? 'Modified 50/50' : '50/50'),
    fallbackSubtitle: gameType === 'chaseTheAce' ? "Draw a card each week for a chance to win the progressive jackpot!" : "Win half the pot while supporting our community.",
    fallbackCta: 'Get Your Tickets',
  })
  if (!mainDraw) return null

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const weeklyPrize = Math.floor(mainDraw?.weekly_prize ?? mainDraw?.secondary_prize ?? 0)
  const charityAmount = jackpotAmount
  const totalPot = jackpotAmount * 2
  const isCta = gameType === 'chaseTheAce'
  const ticketsSold = mainDraw?.total_tickets_sold
  const ticketPrice = mainDraw?.ticket_price ?? 5
  const drawCharityName = mainDraw?.charity_name
  const hasDrawCharityName = Boolean(drawCharityName && String(drawCharityName).trim().toLowerCase() !== 'local charity')
  const charityName = hasDrawCharityName ? drawCharityName : c?.('classic5050.localCharity', 'Local Charity') ?? 'Local Charity'
  const renderCharityName = () => hasDrawCharityName ? charityName : (
    <InlineTranslationEdit
      translationKey="classic5050.localCharity"
      value={c?.('classic5050.localCharity', 'Local Charity')}
      fallback="Local Charity"
      onUpdate={handleTranslationUpdate}
      editMode={editMode}
    />
  )

  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl
  const brandName = siteDetails?.siteName || charityConfig.name
  const defaultPlatformLogo = 'https://cms.tlffs.ca/uploads/header_Logo_4f095fb08b.svg'
  const isDefaultLogo = branding?.logoUrl === defaultPlatformLogo
  const logoUrl = isDefaultLogo ? brandLogo : (branding?.logoUrl || brandLogo)
  const logoAlt = isDefaultLogo ? brandName : (branding?.charityName || brandName || 'Charity Partner')

  return (
    <div>
      <section className="relative flex items-center overflow-hidden bg-charity-gradient-br">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/15 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-white/8 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          <div className="hidden sm:block absolute top-20 left-[10%] opacity-20 animate-bounce" style={{ animationDuration: '3s' }}>
            <DollarSign className="w-16 h-16 text-white" />
          </div>
          <div className="hidden sm:block absolute top-32 right-[15%] opacity-15 animate-bounce" style={{ animationDuration: '4s', animationDelay: '0.5s' }}>
            <Heart className="w-12 h-12 text-white" />
          </div>
          <div className="hidden sm:block absolute bottom-32 left-[20%] opacity-20 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '1s' }}>
            <Gift className="w-14 h-14 text-white" />
          </div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-3 pb-4 sm:pt-[66px] sm:pb-[84px]">
          <HeroSiteLogo tone="light" />
          {showPartner && (
            <div className="mb-4 lg:hidden">
              <HeroPartnerGlassSelect
                className="mx-auto max-w-sm"
                label={c?.('heroPartners.label', 'Partner') ?? 'Partner'}
                placeholder={c?.('heroPartners.choosePartner', 'Choose a partner') ?? 'Choose a partner'}
              />
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Column */}
            <div className="text-center">
              {logoUrl && (
                <div className="mb-2 sm:mb-8 flex justify-center">
                  <div className="relative group">
                    <div className="absolute -inset-3 bg-white/20 rounded-2xl blur-xl opacity-40 group-hover:opacity-60 transition-opacity" />
                    <div className="relative bg-white rounded-xl sm:rounded-2xl px-3 py-2 sm:px-6 sm:py-4 shadow-2xl">
                      <ResponsiveImage src={logoUrl} alt={logoAlt} className="h-8 sm:h-20 lg:h-24 w-auto object-contain max-w-[160px] sm:max-w-[280px]" sizes="(max-width: 640px) 160px, 280px" />
                    </div>
                  </div>
                </div>
              )}

              {/* Live Badge */}
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-4 sm:py-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-full text-[10px] sm:text-sm font-bold mb-2 sm:mb-6">
                <span className="relative flex h-2 w-2 sm:h-3 sm:w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 sm:h-3 sm:w-3 bg-emerald-500" />
                </span>
                <InlineTranslationEdit translationKey={heroContent.eyebrowKey} value={heroContent.eyebrowLabel} fallback="Operated by" onUpdate={handleTranslationUpdate} editMode={editMode} />
                {heroContent.showOperatorName && <span> {heroContent.operatorName}</span>}{' '}
                <InlineTranslationEdit translationKey="classic5050.liveBadgeSeparator" value={c?.('classic5050.liveBadgeSeparator', '—')} fallback="—" onUpdate={handleTranslationUpdate} editMode={editMode} />{' '}
                <InlineTranslationEdit translationKey="classic5050.liveNow" value={c?.('classic5050.liveNow', 'LIVE NOW')} fallback="LIVE NOW" onUpdate={handleTranslationUpdate} editMode={editMode} />
              </div>

              <h1 className="text-2xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-1 sm:mb-4 leading-tight">
                <span className="block">{heroContent.organizationName}</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-white/80 to-white">
                  <InlineTranslationEdit translationKey={heroContent.bannerKey} value={heroContent.bannerText} fallback={heroContent.bannerFallback} onUpdate={handleTranslationUpdate} editMode={editMode} />
                </span>
              </h1>

              <p className="hidden sm:block text-xl md:text-2xl text-white/80 mb-8 max-w-xl mx-auto">
                <InlineTranslationEdit translationKey={heroContent.subtitleKey} value={heroContent.subtitleText} fallback={heroContent.subtitleFallback} onUpdate={handleTranslationUpdate} editMode={editMode} multiline />
              </p>

              {/* Mobile Prize Card */}
              <div className="lg:hidden mb-2 sm:mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-3xl p-2 sm:p-6 border border-white/20">
                  {isCta ? (
                    <>
                      <p className="text-white/70 font-bold uppercase text-[9px] sm:text-sm tracking-wider mb-0.5 sm:mb-4 text-center"><InlineTranslationEdit translationKey="chaseTheAce.progressiveJackpot" value={c?.('chaseTheAce.progressiveJackpot', 'Progressive Jackpot')} fallback="Progressive Jackpot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                      <p className="text-2xl sm:text-5xl md:text-6xl font-black text-white text-center mb-1.5 sm:mb-4">{formatCurrencyValue(jackpotAmount, { language })}</p>
                      {weeklyPrize >= 1 && (
                        <div className="bg-white/20 rounded-md sm:rounded-xl p-1.5 sm:p-3 text-center">
                          <p className="text-[9px] sm:text-xs text-white/70 mb-0 sm:mb-1"><InlineTranslationEdit translationKey="chaseTheAce.weeklyPrize" value={c?.('chaseTheAce.weeklyPrize', 'Weekly Prize')} fallback="Weekly Prize" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-xs sm:text-xl font-black text-white">{formatCurrencyValue(weeklyPrize, { language })}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-white/70 font-bold uppercase text-[9px] sm:text-sm tracking-wider mb-0.5 sm:mb-4 text-center"><InlineTranslationEdit translationKey="classic5050.totalPot" value={c?.('classic5050.totalPot', 'Total Pot')} fallback="Total Pot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                      <p className="text-2xl sm:text-5xl md:text-6xl font-black text-white text-center mb-1.5 sm:mb-4">{formatCurrencyValue(totalPot, { language })}</p>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-4">
                        <div className="bg-white/20 rounded-md sm:rounded-xl p-1.5 sm:p-3 text-center">
                          <p className="text-[9px] sm:text-xs text-white/70 mb-0 sm:mb-1"><InlineTranslationEdit translationKey="classic5050.winnerGets" value={c?.('classic5050.winnerGets', 'Winner Gets')} fallback="Winner Gets" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-xs sm:text-xl font-black text-white">{formatCurrencyValue(jackpotAmount, { language })}</p>
                        </div>
                        <div className="bg-white/20 rounded-md sm:rounded-xl p-1.5 sm:p-3 text-center">
                          <p className="text-[9px] sm:text-xs text-white/70 mb-0 sm:mb-1"><InlineTranslationEdit translationKey="classic5050.charityGets" value={c?.('classic5050.charityGets', 'Charity Gets')} fallback="Charity Gets" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-xs sm:text-xl font-black text-white">{formatCurrencyValue(charityAmount, { language })}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Lifecycle message (draw started / sales closed) — mobile only; desktop shows it in the right column countdown slot */}
              {lifecycleState && (
                <DrawLifecycleMessage state={lifecycleState} compact className="mb-4 lg:hidden" />
              )}

              {/* CTA */}
              <button
                onClick={() => !purchasesBlocked && purchaseNavigate?.('/purchase')}
                disabled={purchasesBlocked}
                className={`group relative w-full sm:w-auto px-5 sm:px-10 py-2.5 sm:py-5 text-sm sm:text-xl font-black rounded-lg sm:rounded-2xl overflow-hidden transition-all ${purchasesBlocked ? 'bg-gray-400 cursor-not-allowed opacity-70' : 'bg-white text-charity-primary hover:scale-105 hover:shadow-2xl'}`}
              >
                <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
                  <Ticket className="w-4 h-4 sm:w-6 sm:h-6" />
                  <InlineTranslationEdit translationKey={heroContent.ctaKey} value={heroContent.ctaText} fallback={heroContent.ctaFallback} onUpdate={handleTranslationUpdate} editMode={editMode} />
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>

              {/* Stats */}
              <div className="mt-2 sm:mt-6 hidden sm:flex flex-wrap justify-center gap-3 sm:gap-6 text-xs sm:text-sm">
                {ticketsSold != null && (
                <div className="flex items-center gap-1.5 sm:gap-2 text-white/70">
                    <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span><strong className="text-white">{ticketsSold.toLocaleString()}</strong> <InlineTranslationEdit translationKey="classic5050.ticketsSoldLabel" value={c?.('classic5050.ticketsSoldLabel', 'tickets sold')} fallback="tickets sold" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 sm:gap-2 text-white/70">
                  <Ticket className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span><strong className="text-white">${ticketPrice}</strong> <InlineTranslationEdit translationKey="classic5050.perTicket" value={c?.('classic5050.perTicket', 'per ticket')} fallback="per ticket" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
                </div>
              </div>
            </div>

            {/* Right Column - Desktop Jackpot Card */}
            <div className="hidden lg:flex flex-col items-center gap-6">
              {showPartner && (
                <HeroPartnerGlassSelect
                  className="w-full max-w-md"
                  label={c?.('heroPartners.label', 'Partner') ?? 'Partner'}
                  placeholder={c?.('heroPartners.choosePartner', 'Choose a partner') ?? 'Choose a partner'}
                />
              )}

              <div className="relative w-full max-w-md">
                <div className="absolute -inset-4 bg-white/20 rounded-3xl blur-xl opacity-50" />
                <div className="relative bg-white/10 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/30">
                  {isCta ? (
                    <>
                      <p className="text-white/70 font-bold uppercase text-sm tracking-wider mb-2 text-center"><InlineTranslationEdit translationKey="chaseTheAce.progressiveJackpot" value={c?.('chaseTheAce.progressiveJackpot', 'Progressive Jackpot')} fallback="Progressive Jackpot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                      <p className="text-6xl xl:text-7xl font-black text-white text-center mb-6">{formatCurrencyValue(jackpotAmount, { language })}</p>
                      {weeklyPrize >= 1 && (
                        <div className="bg-white/20 rounded-xl p-4 text-center border border-white/30">
                          <Trophy className="w-6 h-6 text-white/80 mx-auto mb-2" />
                          <p className="text-xs text-white/70 mb-1"><InlineTranslationEdit translationKey="chaseTheAce.weeklyPrize" value={c?.('chaseTheAce.weeklyPrize', 'Weekly Prize')} fallback="Weekly Prize" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-2xl font-black text-white">{formatCurrencyValue(weeklyPrize, { language })}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-white/70 font-bold uppercase text-sm tracking-wider mb-2 text-center"><InlineTranslationEdit translationKey="classic5050.totalPot" value={c?.('classic5050.totalPot', 'Total Pot')} fallback="Total Pot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                      <p className="text-6xl xl:text-7xl font-black text-white text-center mb-6">{formatCurrencyValue(totalPot, { language })}</p>

                      <div className="relative h-8 bg-gray-900/30 rounded-full overflow-hidden mb-4">
                        <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                          <span className="text-xs font-bold text-white"><InlineTranslationEdit translationKey="classic5050.fiftyYou" value={c?.('classic5050.fiftyYou', '50% YOU')} fallback="50% YOU" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
                        </div>
                        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-r from-teal-500 to-emerald-500 flex items-center justify-center">
                          <span className="text-xs font-bold text-white"><InlineTranslationEdit translationKey="classic5050.fiftyCharity" value={c?.('classic5050.fiftyCharity', '50% CHARITY')} fallback="50% CHARITY" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/20 rounded-xl p-4 text-center border border-white/30">
                          <Trophy className="w-6 h-6 text-white/80 mx-auto mb-2" />
                          <p className="text-xs text-white/70 mb-1"><InlineTranslationEdit translationKey="classic5050.winnerGets" value={c?.('classic5050.winnerGets', 'Winner Gets')} fallback="Winner Gets" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-2xl font-black text-white">{formatCurrencyValue(jackpotAmount, { language })}</p>
                        </div>
                        <div className="bg-white/20 rounded-xl p-4 text-center border border-white/30">
                          <Heart className="w-6 h-6 text-white/80 mx-auto mb-2" />
                          <p className="text-xs text-white/70 mb-1"><InlineTranslationEdit translationKey="classic5050.charityGets" value={c?.('classic5050.charityGets', 'Charity Gets')} fallback="Charity Gets" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                          <p className="text-2xl font-black text-white">{formatCurrencyValue(charityAmount, { language })}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Countdown / lifecycle state */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/20">
                {lifecycleState ? (
                  <DrawLifecycleMessage state={lifecycleState} compact />
                ) : (
                  <>
                    <DrawCountdownHeader
                      draw={mainDraw}
                      className="mb-3"
                      labelClassName="text-white/70"
                    >
                      <InlineTranslationEdit translationKey="classic5050.drawIn" value={c?.('classic5050.drawIn', 'Draw In')} fallback="Draw In" onUpdate={handleTranslationUpdate} editMode={editMode} />
                    </DrawCountdownHeader>
                    <div className="flex justify-center">
                      <CountdownTimerStyles
                        timeLeft={timeLeft}
                        shouldAnimate={shouldAnimate}
                        style={countdownStyle}
                        variant="dark"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jackpot Summary bar */}
      <div className="relative z-20 -mt-12 pb-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="text-center md:text-left">
                {isCta ? (
                  <>
                    <p className="text-sm font-semibold text-charity-primary-dark dark:text-charity-primary-light uppercase tracking-wider"><InlineTranslationEdit translationKey="chaseTheAce.progressiveJackpot" value={c?.('chaseTheAce.progressiveJackpot', 'Progressive Jackpot')} fallback="Progressive Jackpot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                    <p className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(jackpotAmount, { language })}</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-charity-primary-dark dark:text-charity-primary-light uppercase tracking-wider"><InlineTranslationEdit translationKey="classic5050.yourPrize" value={c?.('classic5050.yourPrize', 'Your Prize')} fallback="Your Prize" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                    <p className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(jackpotAmount, { language })}</p>
                  </>
                )}
              </div>
              <div className="text-center border-l border-r border-gray-200 dark:border-gray-700 px-6">
                {isCta ? (
                  <>
                    <p className="text-sm font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wider"><InlineTranslationEdit translationKey="chaseTheAce.weeklyPrize" value={c?.('chaseTheAce.weeklyPrize', 'Weekly Prize')} fallback="Weekly Prize" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                    <p className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(weeklyPrize, { language })}</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wider">{renderCharityName()} <InlineTranslationEdit translationKey="classic5050.gets" value={c?.('classic5050.gets', 'Gets')} fallback="Gets" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                    <p className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(charityAmount, { language })}</p>
                  </>
                )}
              </div>
              <div className="text-center md:text-right">
                <button
                  onClick={() => !purchasesBlocked && purchaseNavigate?.('/purchase')}
                  disabled={purchasesBlocked}
                  className={`inline-flex items-center gap-2 px-6 py-3 font-bold rounded-xl transition-all shadow-lg ${purchasesBlocked ? 'bg-gray-400 cursor-not-allowed opacity-70 text-white' : 'bg-charity-gradient text-white hover:scale-105 hover:shadow-charity-primary/30'}`}
                >
                  <Ticket className="w-5 h-5" />
                  <InlineTranslationEdit translationKey="classic5050.getTickets" value={c?.('classic5050.getTickets', 'Get Tickets')} fallback="Get Tickets" onUpdate={handleTranslationUpdate} editMode={editMode} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Stats & Winners: Impact stats grid + Recent Winners column
export function PremiumStatsWinnersSection({
  mainDraw, t, editMode, handleTranslationUpdate, gameType = 'classic5050', language,
}) {
  const c = t
  if (!mainDraw) return null

  const isCta = gameType === 'chaseTheAce'
  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const weeklyPrize = Math.floor(mainDraw?.weekly_prize ?? mainDraw?.secondary_prize ?? 0)
  const charityAmount = jackpotAmount
  const totalPot = jackpotAmount * 2
  const ticketsSold = mainDraw?.total_tickets_sold

  return (
    <section className="py-12 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-blue-500" />
                <InlineTranslationEdit translationKey="classic5050.drawImpact" value={c?.('classic5050.drawImpact', "This Draw's Impact")} fallback="This Draw's Impact" onUpdate={handleTranslationUpdate} editMode={editMode} />
              </h2>
            </div>

            {isCta ? (
              <div className={`grid sm:grid-cols-2 ${ticketsSold != null ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-4 mb-6`}>
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <DollarSign className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-3xl font-black text-gray-900 dark:text-white">{jackpotAmount > 0 ? formatCurrencyValue(jackpotAmount, { language }) : c?.('chaseTheAce.potBuilding', 'Pot Building')}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400"><InlineTranslationEdit translationKey="chaseTheAce.progressiveJackpot" value={c?.('chaseTheAce.progressiveJackpot', 'Progressive Jackpot')} fallback="Progressive Jackpot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                </div>
                <div className="text-center p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl">
                  <Trophy className="w-8 h-8 text-teal-500 mx-auto mb-2" />
                  <p className="text-3xl font-black text-gray-900 dark:text-white">{weeklyPrize > 0 ? formatCurrencyValue(weeklyPrize, { language }) : c?.('chaseTheAce.potBuilding', 'Pot Building')}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400"><InlineTranslationEdit translationKey="chaseTheAce.weeklyPrize" value={c?.('chaseTheAce.weeklyPrize', 'Weekly Prize')} fallback="Weekly Prize" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                </div>
                {ticketsSold != null && (
                  <div className="text-center p-4 bg-gray-100 dark:bg-gray-900 rounded-xl">
                    <Users className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                    <p className="text-3xl font-black text-gray-900 dark:text-white">{ticketsSold.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400"><InlineTranslationEdit translationKey="classic5050.ticketsSold" value={c?.('classic5050.ticketsSold', 'Tickets Sold')} fallback="Tickets Sold" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className={`grid sm:grid-cols-2 ${ticketsSold != null ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4 mb-6`}>
                  <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                    <DollarSign className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-3xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(totalPot, { language })}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400"><InlineTranslationEdit translationKey="classic5050.totalPot" value={c?.('classic5050.totalPot', 'Total Pot')} fallback="Total Pot" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                  </div>
                  <div className="text-center p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl">
                    <Trophy className="w-8 h-8 text-cyan-500 mx-auto mb-2" />
                    <p className="text-3xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(jackpotAmount, { language })}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400"><InlineTranslationEdit translationKey="classic5050.winnerPrize" value={c?.('classic5050.winnerPrize', 'Winner Prize')} fallback="Winner Prize" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                  </div>
                  <div className="text-center p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl">
                    <Heart className="w-8 h-8 text-teal-500 mx-auto mb-2" />
                    <p className="text-3xl font-black text-gray-900 dark:text-white">{formatCurrencyValue(charityAmount, { language })}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400"><InlineTranslationEdit translationKey="classic5050.forCharity" value={c?.('classic5050.forCharity', 'For Charity')} fallback="For Charity" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                  </div>
                  {ticketsSold != null && (
                    <div className="text-center p-4 bg-gray-100 dark:bg-gray-900 rounded-xl">
                      <Users className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                      <p className="text-3xl font-black text-gray-900 dark:text-white">{ticketsSold.toLocaleString()}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400"><InlineTranslationEdit translationKey="classic5050.ticketsSold" value={c?.('classic5050.ticketsSold', 'Tickets Sold')} fallback="Tickets Sold" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
                    </div>
                  )}
                </div>

                {/* Split Pot Bar */}
                <div className="bg-gray-100 dark:bg-gray-900 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <span className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold">
                      <Trophy className="w-4 h-4" /> <InlineTranslationEdit translationKey="classic5050.winnerShareLabel" value={c?.('classic5050.winnerShareLabel', 'Winner: 50%')} fallback="Winner: 50%" onUpdate={handleTranslationUpdate} editMode={editMode} />
                    </span>
                    <span className="flex items-center gap-2 text-teal-600 dark:text-teal-400 font-semibold">
                      <InlineTranslationEdit translationKey="classic5050.charityShareLabel" value={c?.('classic5050.charityShareLabel', 'Charity: 50%')} fallback="Charity: 50%" onUpdate={handleTranslationUpdate} editMode={editMode} /> <Heart className="w-4 h-4" />
                    </span>
                  </div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
                    <div className="w-1/2 bg-gradient-to-r from-blue-500 to-cyan-500" />
                    <div className="w-1/2 bg-gradient-to-r from-teal-500 to-emerald-500" />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Recent Winners */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 flex flex-col">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-blue-500" />
              <InlineTranslationEdit translationKey="classic5050.recentWinners" value={c?.('classic5050.recentWinners', 'Recent Winners')} fallback="Recent Winners" onUpdate={handleTranslationUpdate} editMode={editMode} />
            </h3>
            <div className="flex-1">
              <RecentWinners maxWinners={5} showViewAll compact vertical />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// Charity Impact: Split pot visualization
export function PremiumCharityImpactSection({
  mainDraw, t, editMode, handleTranslationUpdate,
}) {
  const c = t
  const drawCharityName = mainDraw?.charity_name
  const charityName = drawCharityName && String(drawCharityName).trim().toLowerCase() !== 'local charity'
    ? drawCharityName
    : c?.('classic5050.localCharity', 'Local Charity') ?? 'Local Charity'
  const charityAmount = mainDraw?.current_jackpot ?? 0

  return (
    <section className="py-12 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 flex items-center justify-center gap-2">
          <Heart className="w-8 h-8 text-teal-500" />
          <InlineTranslationEdit translationKey="classic5050.yourImpact" value={c?.('classic5050.yourImpact', 'Your Impact')} fallback="Your Impact" onUpdate={handleTranslationUpdate} editMode={editMode} />
        </h2>
        <SplitPotCharity
          charityName={charityName}
          charityAmount={charityAmount}
          editMode={editMode}
          handleTranslationUpdate={handleTranslationUpdate}
        />
      </div>
    </section>
  )
}

// Final CTA: Gradient footer call-to-action
export function PremiumFinalCTASection({
  mainDraw, t, editMode, handleTranslationUpdate, purchaseNavigate, language,
}) {
  const c = t
  const totalPot = (mainDraw?.current_jackpot ?? 0) * 2

  return (
    <section className="py-16 relative overflow-hidden bg-charity-gradient-br">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-white/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="flex justify-center gap-6 mb-6">
          <Trophy className="w-12 h-12 text-white/80" />
          <Heart className="w-12 h-12 text-white/80" />
        </div>
        <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
          <InlineTranslationEdit translationKey="classic5050.finalCtaTitle" value={c?.('classic5050.finalCtaTitle', 'Win Half. Give Half.')} fallback="Win Half. Give Half." onUpdate={handleTranslationUpdate} editMode={editMode} />
        </h2>
        <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
          <InlineTranslationEdit translationKey="classic5050.currentPot" value={c?.('classic5050.currentPot', 'Current pot:')} fallback="Current pot:" onUpdate={handleTranslationUpdate} editMode={editMode} /> <strong className="text-white">{formatCurrencyValue(totalPot, { language })}</strong>
        </p>
        <button
          onClick={() => purchaseNavigate?.('/purchase')}
          className="inline-flex items-center gap-3 px-10 py-5 bg-white text-charity-primary text-xl font-black rounded-2xl hover:scale-105 transition-all shadow-2xl"
        >
          <Ticket className="w-6 h-6" />
          <InlineTranslationEdit translationKey="classic5050.finalCtaButton" value={c?.('classic5050.finalCtaButton', 'GET YOUR TICKETS NOW')} fallback="GET YOUR TICKETS NOW" onUpdate={handleTranslationUpdate} editMode={editMode} />
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  )
}
